/**
 * DashboardSelector – Searchable combobox for Metabase dashboard resources.
 * NOTE: modal={false} on Popover is required to prevent focus-trap conflicts
 * when this component is rendered inside a Radix Dialog.
 */

import { useState, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ChevronsUpDown, Check, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import type { MetabaseFlatResource } from '@/types/dashboardConfig';

interface DashboardSelectorProps {
  dashboards: MetabaseFlatResource[];
  isLoading: boolean;
  value: string; // compositeKey
  onChange: (compositeKey: string, resource: MetabaseFlatResource) => void;
  disabled?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

export function DashboardSelector({
  dashboards,
  isLoading,
  value,
  onChange,
  disabled,
  error,
  onRetry,
}: DashboardSelectorProps) {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = dashboards.find((d) => d.compositeKey === value);

  const handleSelect = (compositeKey: string) => {
    const resource = dashboards.find((d) => d.compositeKey === compositeKey);
    if (resource) {
      onChange(compositeKey, resource);
      setOpen(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-foreground">
        Metabase Dashboard <span className="text-destructive">*</span>
      </Label>
      {/* Error state — show inline with retry */}
      {error && !isLoading && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{error}</span>
          {onRetry && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto py-0 px-1 text-destructive hover:text-destructive"
              onClick={onRetry}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Retry
            </Button>
          )}
        </div>
      )}
      {/* modal={false} prevents Dialog from trapping focus, enabling scroll + search input */}
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || isLoading || dashboards.length === 0}
            className="w-full justify-between font-normal h-auto min-h-10 py-2 text-left"
          >
            <span className="truncate flex-1 mr-2">
              {isLoading
                ? 'Loading dashboards…'
                : selected
                ? selected.displayName
                : dashboards.length === 0
                ? 'No dashboards available'
                : 'Select Dashboard'}
            </span>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin shrink-0 opacity-50" />
            ) : (
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          side="bottom"
          avoidCollisions={false}
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={true}>
            <div className="sticky top-0 z-10 bg-popover">
              <CommandInput
                placeholder="Search dashboards…"
                onValueChange={() => {
                  // Reset scroll so the first filtered items aren't hidden under the sticky search input
                  if (listRef.current) listRef.current.scrollTop = 0;
                }}
              />
            </div>
            <CommandList
              ref={listRef}
              className="overflow-y-auto overscroll-contain"
              style={{ maxHeight: 300 }}
              onWheel={(e) => e.stopPropagation()}
            >
              <CommandEmpty>No dashboard found.</CommandEmpty>
              <CommandGroup>
                {dashboards.map((item) => (
                  <CommandItem
                    key={item.compositeKey}
                    value={item.displayName}
                    onSelect={() => handleSelect(item.compositeKey)}
                    className="items-start"
                  >
                    <Check
                      className={`mr-2 mt-0.5 h-4 w-4 shrink-0 ${
                        value === item.compositeKey ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    <span className="break-words whitespace-normal line-clamp-4">
                      {item.displayName}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
